import { runProgress } from "@kontaktfix/shared";
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  Skeleton,
  Stat,
  StatusDot,
  Table,
  Td,
  Th,
  Tr,
} from "@kontaktfix/ui";
import {
  ArrowsClockwiseIcon,
  CheckCircleIcon,
  ClockIcon,
  TrayIcon,
  UsersIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Page, PageHeader } from "@/components/page";
import { RecipientStatusLabel } from "@/components/recipient-status";
import { api } from "@/lib/api";
import { toastError } from "@/lib/errors";
import { formatDateTime, formatNumber, formatRelative } from "@/lib/i18n";
import { useCanChangeContacts, useInvalidateData, useMe, useSyncOverview } from "@/lib/queries";
import { ProgressBar, RunProgressText, runTone, StopRunButton } from "@/pages/sync-run";

export function SyncNowButton() {
  const { t } = useTranslation();
  const invalidate = useInvalidateData();
  const [busy, setBusy] = useState(false);
  const canSync = useCanChangeContacts();
  const run = async () => {
    setBusy(true);
    try {
      await api.post("/api/sync/now");
      toast.success(t("sync.syncRequested"));
      await invalidate();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button variant="primary" onClick={run} loading={busy} disabled={!canSync}>
      {!busy && <ArrowsClockwiseIcon />}
      {t("sync.syncNow")}
    </Button>
  );
}

export function SyncPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { data, isLoading } = useSyncOverview();
  const isAdmin = me?.role === "admin";

  if (isLoading || !data) {
    return (
      <Page>
        <Skeleton className="mb-6 h-10 w-64" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      </Page>
    );
  }

  const problems = data.totals.error + data.totals.unreachable;
  const pending = data.totals.pending + data.totals.syncing + data.totals.removing;

  return (
    <Page>
      <PageHeader
        title={t("sync.title")}
        description={t("sync.description")}
        actions={isAdmin && <SyncNowButton />}
      />

      {data.connectionStatus !== "granted" && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning/30 bg-warning-soft px-4 py-3 text-[13px] text-warning">
          <span className="flex items-center gap-2">
            <WarningIcon className="size-4" />
            {t("sync.connectionMissing")}
          </span>
          {isAdmin && (
            <Button size="sm" asChild>
              <Link to="/settings/{-$tab}" params={{ tab: "microsoft" }}>
                {t("sync.connect")}
              </Link>
            </Button>
          )}
        </div>
      )}

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat
          label={t("sync.lastSuccess")}
          value={
            <span className="text-lg">
              {data.lastSuccessAt ? formatRelative(data.lastSuccessAt) : t("common.never")}
            </span>
          }
          hint={
            <span className="flex items-center gap-1.5">
              <StatusDot tone={data.running ? "accent" : "success"} pulse={data.running} />
              {data.running ? t("sync.inProgress") : t("sync.upToDate")}
            </span>
          }
          icon={<ClockIcon />}
        />
        <Stat
          label={t("sync.recipients")}
          value={formatNumber(data.recipientCount)}
          hint={pending ? `${pending} ${t("status.pending")}` : undefined}
          icon={<UsersIcon />}
        />
        <Stat label={t("sync.delivered")} value={formatNumber(data.deliveredCount)} icon={<TrayIcon />} />
        <Stat
          label={t("sync.problems")}
          value={<span className={problems ? "text-warning" : undefined}>{problems}</span>}
          icon={problems ? <WarningIcon /> : <CheckCircleIcon />}
        />
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader title={t("sync.problems")} />
          {!data.problems.length ? (
            <p className="flex items-center gap-2 px-5 py-4 text-[13px] text-fg-muted">
              <CheckCircleIcon className="size-4 text-success" />
              {t("sync.problemsEmpty")}
            </p>
          ) : (
            <div className="divide-y divide-border">
              {data.problems.map((p) => (
                <button
                  type="button"
                  key={p.userId}
                  onClick={() => navigate({ to: "/people/$userId", params: { userId: p.userId } })}
                  className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-subtle"
                >
                  <Avatar name={p.displayName} size={28} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium">{p.displayName}</div>
                    <div className="truncate text-[12px] text-fg-muted">{p.email}</div>
                  </div>
                  <RecipientStatusLabel recipient={p} />
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <CardHeader title={t("sync.runs")} />
          {!data.runs.length ? (
            <p className="px-5 py-4 text-[13px] text-fg-muted">{t("sync.noRuns")}</p>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{t("sync.columns.started")}</Th>
                  <Th>{t("sync.columns.trigger")}</Th>
                  <Th>{t("sync.columns.progress")}</Th>
                  <Th className="hidden sm:table-cell">{t("sync.columns.changes")}</Th>
                  <Th className="text-right">{t("sync.columns.errors")}</Th>
                </tr>
              </thead>
              <tbody>
                {data.runs.map((run) => {
                  const { percent } = runProgress(run);
                  const open = (tab?: string) =>
                    isAdmin &&
                    navigate({ to: "/sync/runs/$runId", params: { runId: run.id }, search: { tab } });
                  return (
                    <Tr key={run.id} onClick={isAdmin ? () => open() : undefined}>
                      <Td className="whitespace-nowrap" title={formatDateTime(run.startedAt)}>
                        <span className="flex items-center gap-2">
                          <StatusDot
                            tone={run.status === "done" && run.errors ? "warning" : runTone(run)}
                            pulse={run.status === "running"}
                          />
                          {formatRelative(run.startedAt)}
                        </span>
                      </Td>
                      <Td>
                        <Badge>{t(`sync.triggers.${run.trigger}`)}</Badge>
                      </Td>
                      <Td>
                        <div className="flex flex-col gap-1">
                          <ProgressBar percent={percent} tone={runTone(run)} className="w-40" />
                          <RunProgressText run={run} />
                          {isAdmin && run.status === "running" && (
                            <span>
                              <StopRunButton
                                compact
                                stop={() => api.post(`/api/sync/runs/${run.id}/cancel`)}
                              />
                            </span>
                          )}
                        </div>
                      </Td>
                      <Td className="hidden font-mono text-[12px] text-fg-muted sm:table-cell">
                        <button
                          type="button"
                          disabled={!isAdmin}
                          onClick={(e) => {
                            e.stopPropagation();
                            open("changes");
                          }}
                          className="rounded px-1 hover:bg-subtle hover:underline disabled:hover:bg-transparent disabled:hover:no-underline"
                          title={isAdmin ? t("sync.run.showChanges") : undefined}
                        >
                          <span className="text-success">+{formatNumber(run.created)}</span> ~
                          {formatNumber(run.updated)}{" "}
                          <span className="text-danger">-{formatNumber(run.deleted)}</span>
                        </button>
                      </Td>
                      <Td className="text-right tabular-nums">
                        {run.errors || <span className="text-fg-subtle">0</span>}
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card>
      </div>
    </Page>
  );
}
